import React from 'react'

import useAccess, { PERMS } from '../useAccess'
import { useEnvironment } from '../../providers/EnvironmentProvider'

const useUserPermissions = () => {
  const hasPermission = useAccess()
  const { user: currentUser } = useEnvironment()

  const hasBasic = React.useMemo(() => hasPermission(PERMS.UsersBasic), [
    hasPermission
  ])

  const hasFull = React.useMemo(() => hasPermission(PERMS.UsersFull), [
    hasPermission
  ])

  const hasAdmin = React.useMemo(() => hasPermission(PERMS.UsersAdmin), [
    hasPermission
  ])

  const canViewProfile = React.useMemo(
    () => hasPermission([PERMS.ContactsAudit, PERMS.UsersBasic]),
    [hasPermission]
  )

  const canListUsers = React.useMemo(
    () => hasPermission([PERMS.UsersBasic, PERMS.UsersSelf]),
    [hasPermission]
  )

  const checkCanSetInternal = React.useCallback(
    (isTeam: boolean, canEdit: boolean) => {
      if (isTeam) {
        return canEdit
      }

      return hasPermission([PERMS.UsersFull, PERMS.UsersAdmin])
    },
    [hasPermission]
  )

  const checkCanSetExternal = React.useCallback(
    (isTeam: boolean, canEdit: boolean, user?: LocalUser) => {
      if (!isTeam) {
        return canEdit
      }

      if (hasPermission(PERMS.ContactsFull)) {
        return true
      }

      if (hasPermission(PERMS.ContactsOwn)) {
        return user?.owner_id === currentUser?.id
      }

      return false
    },
    [hasPermission, currentUser]
  )

  const checkCanCreateUser = React.useCallback(
    (external: boolean) => {
      if (external) {
        return hasPermission([PERMS.ContactsOwn, PERMS.ContactsFull])
      }

      return hasPermission(PERMS.UsersFull)
    },
    [hasPermission]
  )

  const checkCanEditUser = React.useCallback(
    (user?: LocalUser) => {
      if (user?.deleted_at) {
        return false
      }

      return hasPermission([PERMS.UsersFull, PERMS.UsersAdmin])
    },
    [hasPermission]
  )

  const checkCanDeleteUser = React.useCallback(
    (user?: LocalUser) => {
      if (user?.deleted_at) {
        return false
      }

      return hasPermission(PERMS.UsersAdmin)
    },
    [hasPermission]
  )

  return {
    hasBasic,
    hasFull,
    hasAdmin,
    canViewProfile,
    canListUsers,
    checkCanSetInternal,
    checkCanSetExternal,
    checkCanCreateUser,
    checkCanEditUser,
    checkCanDeleteUser
  }
}

export default useUserPermissions
